// frontend/src/components/parent/ParentAIInsightCard.tsx
// Markdown'u okunabilir formata çevirir + AI Mentor soru başlıklarını gösterir

import DOMPurify from 'dompurify';
import { IconLightbulb, IconRefresh } from './ParentIcons';

interface ParentAIInsightCardProps {
  insight: string | null;
  isLoading: boolean;
  onLoad: () => void;
}

/**
 * Basit markdown → JSX dönüştürücü.
 * Gemini'nin ürettiği ## başlık, ** kalın, - liste formatlarını işler.
 */
function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = (key: string) => {
    if (listItems.length === 0) return;
    elements.push(
      <ul key={`ul-${key}`} className="pcd-ai-list">
        {listItems.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
        ))}
      </ul>
    );
    listItems = [];
  };

  const inlineFormat = (str: string): string => {
    const formatted = str
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
    return DOMPurify.sanitize(formatted, {
      ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'code', 'span'],
      ALLOWED_ATTR: []
    });
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList(String(idx));
      return;
    }

    if (trimmed.startsWith('### ')) {
      flushList(String(idx));
      elements.push(<h4 key={idx} className="pcd-ai-h4">{trimmed.slice(4)}</h4>);
    } else if (trimmed.startsWith('## ')) {
      flushList(String(idx));
      elements.push(<h3 key={idx} className="pcd-ai-h3">{trimmed.slice(3)}</h3>);
    } else if (trimmed.startsWith('# ')) {
      flushList(String(idx));
      elements.push(<h2 key={idx} className="pcd-ai-h2">{trimmed.slice(2)}</h2>);
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(trimmed.slice(2));
    } else if (/^\d+\.\s/.test(trimmed)) {
      listItems.push(trimmed.replace(/^\d+\.\s/, ''));
    } else {
      flushList(String(idx));
      elements.push(
        <p key={idx} className="pcd-ai-p"
          dangerouslySetInnerHTML={{ __html: inlineFormat(trimmed) }} />
      );
    }
  });

  flushList('end');
  return elements;
}

export function ParentAIInsightCard({ insight, isLoading, onLoad }: ParentAIInsightCardProps) {
  return (
    <div className="pcd-ai-insight-card">
      <div className="pcd-ai-insight-header">
        <div className="pcd-ai-insight-icon-wrap">
          <IconLightbulb size={22} />
        </div>
        <div className="pcd-ai-insight-title-wrap">
          <h3 className="pcd-section-title" style={{ marginBottom: 2 }}>AI Gelişim Analizi</h3>
          <p className="pcd-ai-insight-subtitle">
            Çocuğunuzun verilerine dayalı kişisel öneriler
          </p>
        </div>
        {(insight || isLoading) && (
          <button
            type="button"
            className="pcd-ai-refresh-btn"
            onClick={onLoad}
            disabled={isLoading}
            title="Yeniden analiz et"
          >
            <IconRefresh size={16} className={isLoading ? 'pcd-spinning' : ''} />
          </button>
        )}
      </div>

      {isLoading && (
        <div className="pcd-ai-loading-state">
          <div className="pcd-ai-pulse-dots">
            <span /><span /><span />
          </div>
          <p>MinikBot, çocuğunuzun verilerini analiz ediyor...</p>
        </div>
      )}

      {!isLoading && insight && (
        <div className="pcd-ai-insight-body pcd-ai-rendered">
          {renderMarkdown(insight)}
        </div>
      )}

      {!isLoading && !insight && (
        <div className="pcd-ai-cta">
          <p className="pcd-ai-cta-text">
            Ders, quiz ve proje verilerine dayalı kişiselleştirilmiş bir AI analizi alın.
          </p>
          <button type="button" className="pcd-ai-cta-btn" onClick={onLoad}>
            <IconLightbulb size={16} />
            AI Analizi Oluştur
          </button>
        </div>
      )}
    </div>
  );
}
